import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  Heading,
  Field,
  InputGroup,
  Spacer,
  Text,
  FormRow,
  HelpIcon,
  Input as GuiInput,
  Message,
  Grid,
  Button,
  Flex,
  CheckBox,
  Select as GuiSelect,
  InputGroupAddon,
} from '@oliasoft-open-source/react-ui-library';
import { round, getUnit, getValue, unum } from '@oliasoft-open-source/units';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { addCostItem, initialCostItem } from 'store/entities/cost-setup/cost-setup';
import { selectCurrencies, selectCompanyCostSetup } from 'src/store/entities/company-settings/selectors';
import { getProjectSettings } from 'store/entities/project-settings/project-settings';
import { getResolver } from 'src/validation/resolver';
import translations from 'src/internationalisation/translation-map.json';
import { costItemsSchemaValidator } from 'schemas/ajv-validators';
import {
  Input,
  CheckBoxList,
  Select,
  NumberInput,
  UnitInput,
  UnitInputConvertable,
  SingleCheckBox,
  TextArea,
  ControlledText,
} from 'client/common/form-inputs';
import {
  convertToInputUnits,
  convertToStorageUnits,
  getStorageUnitByQuantityType,
  getUnitCost,
} from 'client/common/units/units';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import { useAutoSave } from 'client/common/auto-save/use-auto-save';
import { getSelectOptions } from 'client/common/lists/lists';
import { autoSaveWaitShort } from 'src/config/config';
import {
  selectSectionSuggestedValues,
  selectSectionSuggestedValuesForHoleSize,
} from 'store/entities/well-details/selectors';
import { linkFields } from 'src/enums/settings';
import { selectSectionOperations } from 'store/entities/activity-model/selectors';
import { selectPriceBookCompanyItems } from 'src/store/entities/price-book/selectors';
import { routes } from 'routes/routes';
import { generatePath } from 'store/navigation/navigation';
import { useParams } from 'react-router';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import {
  selectCostCodesCodeAndNameToIdMap,
  selectCostCodesIdToCodeAndNameMap,
  selectCostCodesItemOptions,
} from 'src/store/entities/cost-codes/selectors';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { FeatureFlagName } from 'src/enums/feature-flags';
import { Sections } from 'src/enums/sections';
import { mapNameToCode, validateValue } from './helpers/check-for-valid-id';
import { meanAllActivities, meanOperation } from './helpers/precalculate-time';

import './style.css';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';

const linkFieldMap = {
  length: 'linkedValue',
  volume: 'linkedValue',
  holeVolume: 'linkedValue',
  holeLength: 'linkedValue',
};

const CostItemForm = ({
  costItem,
  addCostItem,
  operations,
  projectId,
  projectCurrencySettings,
  isPageDisabled,
  depthUnit,
  volumeUnit,
  suggestedOptionsList,
  sectionOperations,
  suggestedOptionsForSectionHoleVolume,
  costCodesItems,
  costCodesIdToCodeAndNameMap,
  costCodesCodeAndNameToIdMap,
  primaryCurrency,
  getProjectSettings,
  companyCurrenciesOptions,
  priceBookCompanyItems,
  isExtendedMudVolume,
}) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const navigate = useNavigate();

  const isImportedFromPriceBook = !!costItem?.priceBookItemId || !!costItem?.vendor;
  const isLinkedToPriceBook = isImportedFromPriceBook && costItem?.isLinked;
  const isItemDeletedFromPriceBook = costItem?.isLinked && !costItem?.priceBookItemId;
  const linkingMessage = isItemDeletedFromPriceBook
    ? translations.priceBook_thisCostItemIsDeletedFromPriceBook
    : translations.priceBook_thisCostItemIsConnectedToThePriceBook;
  const defaultValues = { ...initialCostItem, currency: primaryCurrency };
  const linkedPriceBook = priceBookCompanyItems.find(
    (item) => costItem?.priceBookItemId && item.priceBookItemId === costItem?.priceBookItemId,
  );

  const isProjectCurrencySettingsActive = FeatureFlags.isActive(FeatureFlagName.UserSetCurrency);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: getResolver(costItemsSchemaValidator),
  });

  const checkedOperationIds = watch('operations') || [];
  const checkedOperations = operations.filter((operation) => checkedOperationIds.includes(operation.id));
  const meanByOperations = meanAllActivities(checkedOperations);
  const totalMeanHours = meanByOperations.reduce((sum, hours) => sum + hours, 0);
  const totalMeanDays = round(unum(totalMeanHours, 'd', 'h'), 2);

  const {
    type,
    currency,
    quantity,
    length,
    volume,
    unitCost,
    linkedValue,
    itemCount,
    holeVolume,
    isDistributed = false,
    isStandalone = false,
    excessNumber,
    holeLength,
    mudVolume = false,
    surfaceSystem,
    losses,
    dilution,
    extraTimeValue,
    extraTimeUnit,
    cost,
  } = watch();
  const isLengthQuantity = quantity === QuantityType.Length || quantity === QuantityType.HoleLength;
  const quantityUnit = isLengthQuantity ? depthUnit : volumeUnit;

  const suggestedOptions = React.useMemo(
    () =>
      suggestedOptionsList.map((option) =>
        convertToInputUnits(option, ['value'], isLengthQuantity ? 'm' : 'm3', quantityUnit, true, 2),
      ),
    [suggestedOptionsList, quantity, quantityUnit],
  );
  const holeLengthSuggestedOptions = React.useMemo(() => {
    const allowedSections = [
      Sections.Conductor,
      Sections.Surface,
      Sections.Intermediate,
      Sections.Production,
      Sections.PilotHole,
      Sections.Reservoir,
    ];
    return suggestedOptions.filter((option) => allowedSections.includes(option.sectionName));
  }, [suggestedOptions]);

  const currenciesOptions = isProjectCurrencySettingsActive
    ? React.useMemo(() => {
        const primaryCurr = projectCurrencySettings?.primaryCurrency;
        const additionalCurr = projectCurrencySettings?.additionalCurrencies?.map((item) => item.currency);
        return [primaryCurr, ...(additionalCurr || [])].filter(Boolean);
      }, [projectCurrencySettings])
    : companyCurrenciesOptions;

  useEffect(() => {
    const costData = costItem || defaultValues;
    if (costData) {
      let convertedCostData = convertToInputUnits(costData, ['length', 'holeLength'], 'm', depthUnit, true);
      convertedCostData = convertToInputUnits(convertedCostData, ['volume', 'holeVolume'], 'm3', volumeUnit, true);

      convertedCostData.unitCost = getStorageUnitByQuantityType(costData)
        ? unum(
            convertedCostData.unitCost,
            getStorageUnitByQuantityType(convertedCostData),
            getUnitCost(convertedCostData, depthUnit, volumeUnit),
          )
        : convertedCostData.unitCost;
      convertedCostData.excessNumber = getStorageUnitByQuantityType(costData)
        ? unum(
            convertedCostData.excessNumber,
            getUnitCost(convertedCostData, depthUnit, volumeUnit),
            getStorageUnitByQuantityType(convertedCostData),
          )
        : convertedCostData.excessNumber;
      reset({
        ...convertedCostData,
        length: convertedCostData.length || `0|${depthUnit}`,
        holeLength: convertedCostData.holeLength || `0|${depthUnit}`,
        volume: convertedCostData.volume || `0|${volumeUnit}`,
        holeVolume: convertedCostData.holeVolume || `0|${volumeUnit}`,
        costCodesId: validateValue(costData.costCodesId, costCodesItems, costCodesIdToCodeAndNameMap),
        excessNumber: convertedCostData.excessNumber || 0,
        surfaceSystem: convertedCostData.surfaceSystem || 0,
        losses: convertedCostData.losses || 0,
        dilution: convertedCostData.dilution || 0,
      });
    }
  }, [costItem?.costItemId, suggestedOptions.length, suggestedOptionsForSectionHoleVolume.length, reset]);

  useEffect(() => {
    getProjectSettings(projectId);
  }, [projectId]);

  const debounceAddCostItem = useRef(debounce(addCostItem, autoSaveWaitShort));
  const onSubmit = handleSubmit((data) => {
    if (!isDirty) return;
    data.projectId = projectId;
    const mappedCostCodesId = mapNameToCode(data.costCodesId, costCodesCodeAndNameToIdMap);
    data.costCodesId = mappedCostCodesId ?? null;
    const convertedCost = convertToStorageUnits(data, ['length', 'holeLength', 'volume', 'holeVolume'], {
      length: 'm',
      holeLength: 'm',
      volume: 'm3',
      holeVolume: 'm3',
    });
    convertedCost.excessNumber = getStorageUnitByQuantityType(data)
      ? unum(convertedCost.excessNumber, getStorageUnitByQuantityType(data), getUnitCost(data, depthUnit, volumeUnit))
      : data.excessNumber;
    convertedCost.unitCost = getStorageUnitByQuantityType(data)
      ? unum(convertedCost.unitCost, getUnitCost(data, depthUnit, volumeUnit), getStorageUnitByQuantityType(data))
      : data.unitCost;
    debounceAddCostItem.current(convertedCost);
  });
  useAutoSave(onSubmit, watch);

  const typeList = [
    { label: t(translations.costSetup_dayRate), value: CostType.DayRate },
    { label: t(translations.costSetup_lumpSum), value: CostType.LumpSum },
    { label: t(translations.costSetup_quantity), value: CostType.Quantity },
  ];

  const quantityList = [
    {
      label: t(translations.costSetup_itemCount),
      value: QuantityType.ItemCount,
    },
    {
      label: t(translations.costSetup_tubularLength),
      value: QuantityType.Length,
      icon: <HelpIcon text={t(translations.costSetup_tubularLengthTooltip)} />,
    },
    {
      label: t(translations.costSetup_holeLength),
      value: QuantityType.HoleLength,
    },
    {
      label: t(translations.costSetup_annularCementVolume),
      value: QuantityType.Volume,
      icon: <HelpIcon text={t(translations.costSetup_annularCementVolumeTooltip)} />,
    },
    {
      label: t(translations.constSetup_sectionHoleVolume),
      value: QuantityType.HoleVolume,
      icon: <HelpIcon text={t(translations.costSetup_sectionHoleVolumeTooltip)} />,
    },
  ];

  const extraTime = [
    { label: t(translations.hours), value: 'hours' },
    { label: t(translations.days), value: 'days' },
  ];

  const selectedUnit = () => {
    switch (quantity) {
      case QuantityType.Length:
        return depthUnit;

      case QuantityType.Volume:
        return getUnit(volume) || volumeUnit;

      case QuantityType.ItemCount:
        return 'item';

      case QuantityType.HoleVolume:
        return getUnit(volume) || volumeUnit;

      case QuantityType.HoleLength:
        return depthUnit;

      default:
        return null;
    }
  };

  const getCurrenciesOptionsWithUnits = (unit) => {
    return currenciesOptions.map((currency) => ({
      label: `${currency.label}${unit ? `/${unit}` : ''}`,
      value: currency.value,
    }));
  };

  const onQuantityChange = () => {
    setValue('linkedValue', linkFields.CUSTOM);
    setValue('length', `0|${depthUnit}`);
    setValue('volume', `0|${volumeUnit}`);
    setValue('holeVolume', `0|${volumeUnit}`);
    setValue('holeLength', `0|${depthUnit}`);
  };

  const calculateTotalCost = () => {
    const excessNumberValue = round(Number(getValue(excessNumber)), 2);
    const lengthValue = round(Number(getValue(length)), 2);
    const volumeValue = round(Number(getValue(volume)), 2);
    const holeVolumeValue = round(Number(getValue(holeVolume)), 2);
    const itemCountValue = round(Number(getValue(itemCount)), 2);
    const holeLengthValue = round(Number(getValue(holeLength)), 2);
    const surfaceSystemValue = round(Number(getValue(surfaceSystem)), 2);
    const lossesValue = round(Number(getValue(losses)), 2);
    const dilutionValue = round(Number(getValue(dilution)), 2);
    const extendedMudVolume = mudVolume ? surfaceSystemValue + lossesValue + dilutionValue : 0;

    switch (quantity) {
      case QuantityType.Length:
        return round((lengthValue + excessNumberValue) * unitCost, 2);
      case QuantityType.Volume:
        return round((volumeValue + excessNumberValue + extendedMudVolume) * unitCost, 2);
      case QuantityType.ItemCount:
        return round((itemCountValue + excessNumberValue) * unitCost, 2);
      case QuantityType.HoleVolume:
        return round((holeVolumeValue + excessNumberValue + extendedMudVolume) * unitCost, 2);
      case QuantityType.HoleLength:
        return round((holeLengthValue + excessNumberValue) * unitCost, 2);
      default:
        return null;
    }
  };

  const calculateDayRateTotalCost = () => {
    const extraDays =
      extraTimeUnit === 'hours' ? round(unum(Number(extraTimeValue) || 0, 'd', 'h'), 2) : Number(extraTimeValue) || 0;
    const totalDays = totalMeanDays + extraDays;
    return totalDays * Number(cost || 0);
  };

  return (
    <>
      <Grid gap>
        <Heading top>{watch('name')}</Heading>
        {isImportedFromPriceBook && (
          <Message
            message={{
              content: (
                <Flex justifyContent={'space-between'} gap="var(--padding)" alignItems="center">
                  <div style={{ flex: 1 }} data-testid="cost-form-message-linking">
                    <Text>
                      {t(
                        isLinkedToPriceBook
                          ? linkingMessage
                          : translations.priceBook_thisCostItemIsNotConnectedToThePriceBook,
                      )}
                    </Text>
                  </div>
                  test
                  {!isItemDeletedFromPriceBook && isLinkedToPriceBook && (
                    <span style={{ flexShrink: 0 }}>
                      <Button
                        small
                        label={t(translations.costSetup_viewPriceBook)}
                        testId="cost-form-button-view-price-book"
                        onClick={() => {
                          const path = generatePath(routes.companySettings.nested.priceBook, { company }, true);
                          const navigateState = linkedPriceBook
                            ? {
                                priceBookId: linkedPriceBook.priceBookId,
                                name: linkedPriceBook.priceBookName,
                              }
                            : null;
                          navigate(path, {
                            state: navigateState,
                          });
                        }}
                      />
                    </span>
                  )}
                </Flex>
              ),
              type: 'Info',
              visible: true,
            }}
          />
        )}
      </Grid>
      <Spacer />
      <form>
        <FormRow>
          <Field label={t(translations.name)}>
            <Input
              testId="cost-form-input-name"
              name="name"
              control={control}
              errors={errors}
              width="600px"
              disabled={isPageDisabled || isImportedFromPriceBook}
            />
          </Field>
          <Field label={t(translations.type)}>
            <Select
              testId="cost-form-select-type"
              name="type"
              control={control}
              errors={errors}
              options={typeList}
              onChange={() => {
                setValue('currency', primaryCurrency);
                setValue('isStandalone', false);
              }}
              width="150px"
              disabled={isPageDisabled || isImportedFromPriceBook}
            />
          </Field>
          <Field label={t(translations.code)}>
            <Select
              testId="cost-form-select-code"
              name="costCodesId"
              control={control}
              errors={errors}
              options={costCodesItems}
              width="165px"
              disabled={isPageDisabled}
              clearable
            />
          </Field>
        </FormRow>

        {type !== CostType.Quantity && (
          <FormRow>
            {type === CostType.DayRate && (
              <>
                <Field label={t(translations.time)} helpText={t(translations.costSetup_timeTooltip)}>
                  <InputGroup width="140px">
                    <GuiInput testId="cost-form-input-time" name="timeValue" value={totalMeanDays} disabled />
                    <InputGroupAddon>{t(translations.days)}</InputGroupAddon>
                  </InputGroup>
                </Field>
                <Field
                  label={t(translations.costSetup_addExtraTime)}
                  helpText={t(translations.costSetup_extraTimeTooltip)}
                >
                  <InputGroup width="160px">
                    <NumberInput
                      testId="cost-form-input-extra-time"
                      name="extraTimeValue"
                      control={control}
                      errors={errors}
                      width="80px"
                      disabled={isPageDisabled}
                    />
                    <Select
                      testId="cost-form-select-extra-time-unit"
                      name="extraTimeUnit"
                      control={control}
                      errors={errors}
                      options={extraTime}
                      width="80px"
                      disabled={isPageDisabled}
                    />
                  </InputGroup>
                </Field>
              </>
            )}
            <Field label={type === CostType.DayRate ? t(translations.costSetup_unitCost) : t(translations.cost)}>
              <InputGroup width="180px">
                <NumberInput
                  testId={`cost-form-input-${convertToKebabCase(
                    type === CostType.DayRate ? t(translations.costSetup_unitCost) : t(translations.cost),
                  )}`}
                  name="cost"
                  control={control}
                  errors={errors}
                  width="80px"
                  disabled={isPageDisabled || isImportedFromPriceBook}
                />
                {type === CostType.DayRate ? (
                  <Select
                    testId="cost-form-select-unit-cost-currency"
                    name="currency"
                    control={control}
                    errors={errors}
                    options={getCurrenciesOptionsWithUnits(`${t(translations.days)}`)}
                    width="100px"
                    disabled={isPageDisabled || isImportedFromPriceBook}
                  />
                ) : (
                  <Select
                    testId="cost-form-select-currency"
                    name="currency"
                    control={control}
                    errors={errors}
                    options={currenciesOptions}
                    width="80px"
                    disabled={isPageDisabled || isImportedFromPriceBook}
                  />
                )}
              </InputGroup>
            </Field>
            {type === CostType.DayRate && (
              <Field label={t(translations.costSetup_totalCost)}>
                <InputGroup>
                  <GuiInput
                    name="totalDayRateCost"
                    disabled={true}
                    value={round(calculateDayRateTotalCost(), 2)}
                    width="100px"
                  />
                  <InputGroupAddon>{currency}</InputGroupAddon>
                </InputGroup>
              </Field>
            )}
          </FormRow>
        )}

        {type === CostType.Quantity && (
          <FormRow>
            <Field label={t(translations.costSetup_quantity)}>
              <Select
                testId="cost-form-select-quantity"
                name="quantity"
                control={control}
                errors={errors}
                onChange={onQuantityChange}
                options={quantityList}
                width="200px"
                disabled={isPageDisabled || isImportedFromPriceBook}
              />
            </Field>
            {quantity === QuantityType.Length && (
              <Field label={t(translations.length)}>
                <UnitInputConvertable
                  testId="cost-form-input-length"
                  name="length"
                  unitkey={QuantitiesTypes.Depth}
                  control={control}
                  errors={errors}
                  predefinedOptions={suggestedOptions}
                  selectedPredefinedOptionKey={linkedValue}
                  onPredefinedChange={setValue}
                  linkFieldMap={linkFieldMap}
                  disabled={isPageDisabled}
                  disabledUnit
                  unit={depthUnit}
                  unitTemplate={{ [QuantitiesTypes.Depth]: depthUnit }}
                  width="220px"
                />
              </Field>
            )}
            {quantity === QuantityType.HoleLength && (
              <Field label={t(translations.length)}>
                <UnitInputConvertable
                  testId="cost-form-input-length"
                  name="holeLength"
                  unitkey={QuantitiesTypes.Depth}
                  control={control}
                  errors={errors}
                  predefinedOptions={holeLengthSuggestedOptions}
                  selectedPredefinedOptionKey={linkedValue}
                  onPredefinedChange={setValue}
                  linkFieldMap={linkFieldMap}
                  disabled={isPageDisabled}
                  disabledUnit
                  unit={depthUnit}
                  unitTemplate={{ [QuantitiesTypes.Depth]: depthUnit }}
                  width="220px"
                />
              </Field>
            )}
            {quantity === QuantityType.Volume && (
              <Field label={t(translations.costSetup_volume)}>
                <UnitInputConvertable
                  testId="cost-form-input-volume"
                  name="volume"
                  unitkey={QuantitiesTypes.Volume}
                  control={control}
                  errors={errors}
                  predefinedOptions={suggestedOptions}
                  selectedPredefinedOptionKey={linkedValue}
                  linkFieldMap={linkFieldMap}
                  onPredefinedChange={setValue}
                  disabled={isPageDisabled}
                  disabledUnit
                  unit={volumeUnit}
                  unitTemplate={{ volume: volumeUnit }}
                  width="220px"
                />
              </Field>
            )}
            {quantity === QuantityType.ItemCount && (
              <Field label={t(translations.costSetup_itemCount)}>
                <NumberInput
                  name="itemCount"
                  control={control}
                  errors={errors}
                  disabled={isPageDisabled}
                  width="210px"
                />
              </Field>
            )}
            {quantity === QuantityType.HoleVolume && (
              <Field label={t(translations.costSetup_volume)}>
                <UnitInputConvertable
                  testId="cost-form-input-volume"
                  name="holeVolume"
                  unitkey={QuantitiesTypes.Volume}
                  control={control}
                  errors={errors}
                  predefinedOptions={suggestedOptionsForSectionHoleVolume}
                  selectedPredefinedOptionKey={linkedValue}
                  onPredefinedChange={setValue}
                  linkFieldMap={linkFieldMap}
                  disabled={isPageDisabled}
                  unit={volumeUnit}
                  unitTemplate={{ volume: volumeUnit }}
                  disabledUnit
                  width="220px"
                />
              </Field>
            )}
            <Field label={t(translations.costSetup_addExcess)}>
              <UnitInput
                testId="cost-form-input-add-excess"
                name="excessNumber"
                unit={selectedUnit()}
                control={control}
                errors={errors}
                disabled={isPageDisabled}
                width="150px"
                enableDisplayRounding
              />
            </Field>
            <Field label={t(translations.costSetup_unitCost)}>
              <InputGroup width="180px">
                <NumberInput
                  testId="cost-form-input-unit-cost"
                  name="unitCost"
                  control={control}
                  errors={errors}
                  width="80px"
                  disabled={isPageDisabled || isImportedFromPriceBook}
                  enableDisplayRounding
                />
                <Select
                  testId="cost-form-select-unit-cost-currency"
                  name="currency"
                  control={control}
                  errors={errors}
                  options={getCurrenciesOptionsWithUnits(selectedUnit())}
                  width="100px"
                  disabled={isPageDisabled || isImportedFromPriceBook}
                />
              </InputGroup>
            </Field>
            <Field label={t(translations.costSetup_totalCost)}>
              <InputGroup>
                <GuiInput name="totalCost" disabled={true} value={round(calculateTotalCost(), 2)} width="100px" />
                <InputGroupAddon>{currency}</InputGroupAddon>
              </InputGroup>
            </Field>
          </FormRow>
        )}
        {!!costItem?.vendor && (
          <FormRow>
            <Field label={t(translations.priceBook_vendor)}>
              <Input
                name="vendor"
                control={control}
                errors={errors}
                width="200px"
                disabled={isPageDisabled || isImportedFromPriceBook}
              />
            </Field>
          </FormRow>
        )}
        <Spacer height="var(--padding-xxs)" />
        {type !== CostType.DayRate && (
          <SingleCheckBox
            testId="cost-form-checkbox-distribute-cost"
            control={control}
            errors={errors}
            label={t(translations.costSetup_distributeCost)}
            name="isDistributed"
            checked={isDistributed}
            onChange={(e) => {
              const { checked } = e.target;
              setValue('isDistributed', checked, { shouldDirty: true });
              if (checked) {
                setValue('isStandalone', false);
              }
            }}
          />
        )}
        <Spacer height="var(--padding-xxs)" />
        {type === CostType.LumpSum && (
          <SingleCheckBox
            control={control}
            errors={errors}
            onChange={(e) => {
              const { checked } = e.target;
              setValue('isStandalone', checked, { shouldDirty: true });
              if (checked) {
                setValue('operations', []);
                setValue('isDistributed', false);
              }
            }}
            label={t(translations.costSetup_planningCost)}
            name="isStandalone"
            checked={isStandalone}
            testId="cost-form-checkbox-planning-cost"
          />
        )}
        <Spacer height="var(--padding-xxs)" />
        {isExtendedMudVolume &&
          type === CostType.Quantity &&
          (quantity === QuantityType.HoleVolume || quantity === QuantityType.Volume) && (
            <SingleCheckBox
              control={control}
              errors={errors}
              onChange={(e) => {
                const { checked } = e.target;
                if (!checked) {
                  setValue('surfaceSystem', 0);
                  setValue('losses', 0);
                  setValue('dilution', 0);
                }
              }}
              label={t(translations.costSetup_mudVolumes)}
              name="mudVolume"
              checked={mudVolume}
            />
          )}
        <Spacer height="var(--padding-xs)" />
        {mudVolume && (
          <FormRow>
            <Field label={t(translations.costSetup_surfaceSystem)}>
              <UnitInput
                name="surfaceSystem"
                unit={selectedUnit()}
                control={control}
                errors={errors}
                disabled={isPageDisabled}
                width="150px"
              />
            </Field>
            <Field label={t(translations.costSetup_losses)}>
              <UnitInput
                name="losses"
                unit={selectedUnit()}
                control={control}
                errors={errors}
                disabled={isPageDisabled}
                width="150px"
              />
            </Field>
            <Field label={t(translations.costSetup_dilution)}>
              <UnitInput
                name="dilution"
                unit={selectedUnit()}
                control={control}
                errors={errors}
                disabled={isPageDisabled}
                width="150px"
              />
            </Field>
          </FormRow>
        )}

        <Field label={t(translations.notes_notes)}>
          <TextArea name="notes" control={control} errors={errors} rows={5} disabled={isPageDisabled} />
        </Field>
        <Spacer height="var(--padding-xs)" />
        <Heading>{t(translations.risks_applyToOperations)}</Heading>
        <Spacer height="var(--padding-xs)" />
        <ControlledText
          testId="cost-form-button-check-all"
          name="operations"
          control={control}
          disabled={isPageDisabled || isStandalone}
          onClick={(field) => {
            const allOperationIds = operations.map((operation) => operation.id);
            field.onChange(allOperationIds);
          }}
          text={t(translations.checkAll)}
        />
        <Spacer width="var(--padding-sm)" />
        <ControlledText
          name="operations"
          control={control}
          disabled={isPageDisabled || isStandalone}
          onClick={(field) => {
            field.onChange([]);
          }}
          text={t(translations.uncheckAll)}
        />
        <Spacer height="var(--padding-xs)" />
        <Grid columns="1fr 1fr 1fr" gap="var(--padding-xs)">
          {sectionOperations?.map((section) => {
            return (
              <div key={section.projectSectionId} className="section-item">
                <Spacer height="var(--padding-xs)" />
                <Controller
                  name="operations"
                  control={control}
                  render={({ field }) => (
                    <CheckBox
                      testId={`cost-form-checkbox-${convertToKebabCase(section.name)}`}
                      onChange={(e) => {
                        const sectionIds = section.items.map((item) => item.id);
                        const currentItems = watch('operations') || [];

                        const nextItems = e.target.checked
                          ? [...new Set([...currentItems, ...sectionIds])]
                          : currentItems.filter((id) => !sectionIds.includes(id));
                        field.onChange(nextItems);
                      }}
                      label={<Text bold>{section.name}</Text>}
                      value={section.sectionId}
                      checked={
                        watch('operations') && section.items.every((item) => watch('operations').includes(item.id))
                      }
                      disabled={isStandalone || isPageDisabled}
                    />
                  )}
                />
                <div style={{ paddingLeft: '24px' }}>
                  <CheckBoxList
                    testId="cost-form"
                    groupName={section.name}
                    items={section.items}
                    control={control}
                    errors={errors}
                    name="operations"
                    disabled={isStandalone || isPageDisabled}
                  />
                </div>
              </div>
            );
          })}
        </Grid>
      </form>
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const { activityModel } = entities;
  const operations = activityModel.operations.map((operation) => ({
    ...operation,
    id: operation.operationId.toString(),
  }));
  const depthUnit = selectUnitByQuantity(entities, QuantitiesTypes.Depth);
  const volumeUnit = selectUnitByQuantity(entities, QuantitiesTypes.Volume);
  const projectCurrencySettings = entities.projectSettings.settings;
  const suggestedOptionsList = selectSectionSuggestedValues({ entities });
  const sectionOperations = selectSectionOperations({ entities });
  const suggestedOptionsForSectionHoleVolume = selectSectionSuggestedValuesForHoleSize({ entities });

  const currencies = selectCurrencies(entities);
  const companyCurrenciesOptions = getSelectOptions(currencies);

  return {
    operations,
    depthUnit,
    volumeUnit,
    suggestedOptionsList,
    sectionOperations,
    suggestedOptionsForSectionHoleVolume,
    costCodesItems: selectCostCodesItemOptions({ entities }),
    costCodesIdToCodeAndNameMap: selectCostCodesIdToCodeAndNameMap({
      entities,
    }),
    costCodesCodeAndNameToIdMap: selectCostCodesCodeAndNameToIdMap({
      entities,
    }),
    projectCurrencySettings,
    companyCurrenciesOptions,
    priceBookCompanyItems: selectPriceBookCompanyItems(entities),
    isExtendedMudVolume: selectCompanyCostSetup(entities)?.mudVolume,
  };
};

const mapDispatchToProps = { addCostItem, getProjectSettings };

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CostItemForm));

export { Container as CostItemForm };
